import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { formatDate, timeAgo, cn } from "@/lib/utils";
import {
  ArrowLeft, Gavel, AlertTriangle, Building2, Wallet, Calendar,
  Check, FileText, ShieldAlert, Trophy, CircleDot,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  draft: "Draft", open_for_bidding: "Open for bidding", awarded: "Awarded",
  in_progress: "In progress", proof_submitted: "Proof submitted", verification: "In verification / dispute",
  completed: "Completed", closed: "Closed", cancelled: "Cancelled",
};

const STATUS_TONE = {
  open_for_bidding: "bg-blue-500/15 text-blue-200 border-blue-400/30",
  awarded: "bg-violet-500/15 text-violet-200 border-violet-400/30",
  in_progress: "bg-amber-500/15 text-amber-200 border-amber-400/30",
  proof_submitted: "bg-cyan-500/15 text-cyan-200 border-cyan-400/30",
  verification: "bg-orange-500/15 text-orange-200 border-orange-400/30",
  completed: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30",
  closed: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30",
  cancelled: "bg-red-500/15 text-red-200 border-red-400/30",
};

const PIPELINE = ["open_for_bidding", "awarded", "in_progress", "proof_submitted", "completed"];

function Stepper({ status }) {
  if (status === "cancelled") {
    return (
      <div className="flex items-center gap-2 text-sm text-red-200">
        <AlertTriangle className="h-4 w-4" /> This tender was cancelled
      </div>
    );
  }
  const effectiveIdx = status === "closed" ? 4 : status === "verification" ? 3 : PIPELINE.indexOf(status);
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {PIPELINE.map((s, i) => {
        const done = i < effectiveIdx || status === "closed";
        const current = i === effectiveIdx && status !== "closed";
        return (
          <div key={s} className="flex items-center gap-1.5">
            <div
              className={cn(
                "h-6 px-2.5 rounded-full text-[11px] font-medium flex items-center gap-1 border transition-colors",
                done ? "bg-white/90 text-[#0b1e3d] border-white/90"
                  : current ? "bg-white/15 text-white border-white/40"
                  : "bg-transparent text-blue-200/50 border-white/15"
              )}
            >
              {done ? <Check className="h-3 w-3" /> : current ? <CircleDot className="h-3 w-3" /> : null}
              {STATUS_LABEL[s]}
            </div>
            {i < PIPELINE.length - 1 && <div className={cn("h-px w-4", done ? "bg-white/60" : "bg-white/15")} />}
          </div>
        );
      })}
    </div>
  );
}

function SectionCard({ icon: Icon, title, tone = "slate", children, testId }) {
  const toneMap = {
    slate: "border-slate-200",
    blue: "border-blue-200",
    orange: "border-orange-200",
  };
  return (
    <Card className={cn(toneMap[tone], "shadow-sm")} data-testid={testId}>
      <CardContent className="p-6 space-y-4">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-[15px]">
          {Icon && <Icon className={cn("h-4 w-4", tone === "orange" ? "text-orange-600" : "text-blue-600")} />}
          {title}
        </h3>
        {children}
      </CardContent>
    </Card>
  );
}

export default function TenderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { org, user } = useAuth();
  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState("");
  const [bidMessage, setBidMessage] = useState("");
  const [proofText, setProofText] = useState("");
  const [disputeReason, setDisputeReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/marketplace/tenders/${id}`).then((r) => setTender(r.data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || err.message))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setBusy(true);
    try {
      await fn();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="text-slate-500 text-sm max-w-4xl mx-auto">Loading…</p>;
  if (!tender) return null;

  const isPoster = tender.poster_org_id === org?.id;
  const isAwarded = tender.awarded_org_id === org?.id;
  const canActAsPoster = isPoster && ["owner", "department_admin"].includes(user?.role);
  const canActAsAwarded = isAwarded && ["owner", "department_admin"].includes(user?.role);
  const canBid = !isPoster && tender.status === "open_for_bidding" && ["owner", "department_admin"].includes(user?.role);
  const openBids = (tender.bids || []).filter((b) => b.status === "open").length;

  const placeBid = () => act(async () => {
    await api.post(`/marketplace/tenders/${id}/bids`, { amount: Number(bidAmount) || 0, message: bidMessage });
    toast.success("Bid placed");
    setBidAmount(""); setBidMessage("");
  });

  const award = (bidId) => act(async () => {
    await api.post(`/marketplace/tenders/${id}/award`, { bid_id: bidId });
    toast.success("Tender awarded");
  });

  const submitProof = () => act(async () => {
    await api.post(`/marketplace/tenders/${id}/proof`, { submission_text: proofText, links: [], file_ids: [] });
    toast.success("Proof submitted");
    setProofText("");
  });

  const verify = (action) => act(async () => {
    await api.post(`/marketplace/tenders/${id}/verify`, { action });
    toast.success(action === "approve" ? "Verified and completed" : "Proof rejected");
  });

  const cancel = (fault) => act(async () => {
    await api.post(`/marketplace/tenders/${id}/cancel`, { fault_attribution: fault });
    toast.success("Tender cancelled");
  });

  const reassign = (fault) => act(async () => {
    await api.post(`/marketplace/tenders/${id}/reassign`, { fault_attribution: fault });
    toast.success("Tender reassigned");
  });

  const dispute = () => act(async () => {
    await api.post(`/marketplace/tenders/${id}/dispute`, { reason: disputeReason });
    toast.success("Dispute opened — work is paused pending arbitration");
    setDisputeReason("");
  });

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Header band — mirrors the marketplace hero so a tender reads as a deal, not a list item */}
      <div className="rounded-3xl bg-gradient-to-br from-[#0b1e3d] via-[#0f2a52] to-[#123a6e] text-white p-8 sm:p-10 shadow-xl shadow-blue-950/10 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-blue-400/10 blur-3xl" />
        <div className="relative space-y-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-blue-200 mb-2">{tender.category}</div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter max-w-xl">{tender.title}</h1>
              <p className="text-blue-200/70 text-sm mt-2">Posted {timeAgo(tender.created_at)}</p>
            </div>
            <Badge className={cn("border text-sm px-3 py-1 shrink-0", STATUS_TONE[tender.status] || "bg-white/10 text-white border-white/20")} data-testid="tender-status">
              {STATUS_LABEL[tender.status] || tender.status}
            </Badge>
          </div>

          <Stepper status={tender.status} />

          <div className="flex flex-wrap gap-8 pt-2">
            <div>
              <div className="flex items-center gap-1.5 text-blue-200/70 text-xs mb-1"><Wallet className="h-3.5 w-3.5" /> Budget</div>
              <div className="font-mono text-xl font-semibold">{tender.budget_value || "—"}</div>
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-blue-200/70 text-xs mb-1"><Calendar className="h-3.5 w-3.5" /> Due date</div>
              <div className="font-mono text-xl font-semibold">{tender.due_date ? formatDate(tender.due_date) : "—"}</div>
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-blue-200/70 text-xs mb-1"><Gavel className="h-3.5 w-3.5" /> Bids</div>
              <div className="font-mono text-xl font-semibold">{tender.bids?.length ?? 0}</div>
            </div>
            {isPoster && (
              <Link to={`/app/marketplace/orgs/${org?.id}`} className="ml-auto self-end">
                <Button variant="outline" size="sm" className="border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white gap-1.5">
                  <Trophy className="h-3.5 w-3.5 text-amber-300" /> Our rank
                </Button>
              </Link>
            )}
          </div>

          {tender.fault_attribution && (
            <div className="inline-flex items-center gap-1.5 rounded-full bg-red-500/15 border border-red-400/30 text-red-200 text-xs px-3 py-1">
              <AlertTriangle className="h-3.5 w-3.5" /> Fault attribution: {tender.fault_attribution.replace(/_/g, " ")}
            </div>
          )}
        </div>
      </div>

      <SectionCard icon={FileText} title="Scope">
        <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{tender.description}</p>
        {tender.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {tender.tags.map((t) => <Badge key={t} variant="secondary">{t}</Badge>)}
          </div>
        )}
      </SectionCard>

      {canBid && (
        <SectionCard icon={Gavel} title="Place a bid" tone="blue" testId="bid-form">
          <div className="grid grid-cols-3 gap-3">
            <Input type="number" min="0" placeholder="Amount" value={bidAmount}
              onChange={(e) => setBidAmount(e.target.value)} data-testid="bid-amount" className="col-span-1" />
            <Input placeholder="Message (optional)" value={bidMessage}
              onChange={(e) => setBidMessage(e.target.value)} data-testid="bid-message" className="col-span-2" />
          </div>
          <Button onClick={placeBid} disabled={busy} data-testid="bid-submit" className="bg-blue-600 hover:bg-blue-700">
            Submit bid
          </Button>
        </SectionCard>
      )}

      {tender.bids?.length > 0 && (
        <SectionCard icon={Gavel} title={`Bids (${tender.bids.length}${openBids ? ` · ${openBids} open` : ""})`}>
          <div className="divide-y divide-slate-100 -mx-2">
            {[...tender.bids].sort((a, b) => a.amount - b.amount).map((b) => (
              <div key={b.id} className="flex items-center gap-3 px-2 py-3" data-testid={`bid-${b.id}`}>
                <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                  <Building2 className="h-4 w-4 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-lg font-semibold text-slate-900">{b.amount}</span>
                    <Badge variant={b.status === "open" ? "outline" : "secondary"} className="capitalize">{b.status}</Badge>
                  </div>
                  {b.message && <div className="text-xs text-slate-500 mt-0.5">{b.message}</div>}
                </div>
                {canActAsPoster && tender.status === "open_for_bidding" && b.status === "open" && (
                  <Button size="sm" onClick={() => award(b.id)} disabled={busy} data-testid={`award-${b.id}`} className="bg-blue-600 hover:bg-blue-700 shrink-0">
                    Award
                  </Button>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {canActAsAwarded && ["awarded", "in_progress"].includes(tender.status) && (
        <SectionCard icon={FileText} title="Submit proof of work" tone="blue">
          <Textarea rows={4} value={proofText} onChange={(e) => setProofText(e.target.value)} data-testid="proof-text" />
          <Button onClick={submitProof} disabled={busy || !proofText} data-testid="proof-submit" className="bg-blue-600 hover:bg-blue-700">
            Submit proof
          </Button>
        </SectionCard>
      )}

      {canActAsPoster && tender.status === "proof_submitted" && (
        <SectionCard icon={ShieldAlert} title="Verify submitted work" tone="blue">
          <div className="flex gap-3">
            <Button onClick={() => verify("approve")} disabled={busy} data-testid="verify-approve" className="bg-emerald-600 hover:bg-emerald-700">
              <Check className="h-4 w-4 mr-1" /> Approve & complete
            </Button>
            <Button onClick={() => verify("reject")} disabled={busy} variant="outline" data-testid="verify-reject">
              Reject
            </Button>
          </div>
        </SectionCard>
      )}

      {(canActAsPoster || canActAsAwarded) && ["in_progress", "proof_submitted", "verification"].includes(tender.status) && (
        <SectionCard icon={AlertTriangle} title="Conflict actions" tone="orange">
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider">Open a dispute (pauses work, escalates to arbitration)</Label>
            <div className="flex gap-2">
              <Input value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} placeholder="Reason" data-testid="dispute-reason" />
              <Button onClick={dispute} disabled={busy || !disputeReason} variant="outline">Escalate</Button>
            </div>
          </div>
          {canActAsPoster && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-orange-100">
              <Button size="sm" variant="outline" onClick={() => cancel("requestor_no_fault")} disabled={busy} data-testid="cancel-nofault">
                Cancel (no-fault)
              </Button>
              <Button size="sm" variant="outline" onClick={() => cancel("vendor_non_performance")} disabled={busy} data-testid="cancel-fault">
                Cancel (vendor at fault)
              </Button>
              <Button size="sm" variant="outline" onClick={() => reassign("vendor_non_performance")} disabled={busy} data-testid="reassign-btn">
                Reassign to next bidder
              </Button>
            </div>
          )}
        </SectionCard>
      )}
    </div>
  );
}
